/*
 * Governance Tile · numbers-reconcile block.
 *
 * Ports mockup v8.8.12 lines 666-673 into React and live-wires every
 * numeric value to /api/admin/governance-tile/summary:
 *   · Bold headline paragraph (static · this-session-scoped)
 *   · One body paragraph whose bold inline is the live tasks_total
 *   · Four itemised lines — each 'X of Y' strong span is resolved
 *     from the endpoint via RECONCILE_ITEMS[i].extract(summary), or
 *     renders an em dash with a `-pending` testid marker when either
 *     operand is unmeasured (attestation-honesty rule · anchor
 *     GB-013 · § V.6).
 *
 * IJ-082 · 2026-07-24 · Phase 2 of the v4-to-v8.8.12 live-tile re-cut.
 * IJ-088 · 2026-07-24 · Stale "48" purge + full live-wire per operator
 *          directive ("48 is last session's number").
 */
import {
  RECONCILE_HEADLINE,
  RECONCILE_BODY_PREFIX,
  RECONCILE_BODY_SUFFIX,
  RECONCILE_BODY_ZERO,
  RECONCILE_ITEMS,
} from './copy/numbersReconcile';

const PENDING = '\u2014';

export default function NumbersReconcile({ summary, loading, error }) {
  const workingSummary = error ? null : summary;
  const tasksTotal = workingSummary?.tasks_total ?? null;

  return (
    <div
      data-testid="governance-tile-numbers-reconcile"
      className="rounded-lg border-l-4 border-cyan-500/50 bg-slate-950/40 px-5 py-4 my-6 text-[13px] leading-relaxed text-slate-300"
    >
      <p className="mb-2">
        <strong className="text-slate-100">{RECONCILE_HEADLINE}</strong>
      </p>
      {tasksTotal === 0 ? (
        <p
          data-testid="governance-tile-reconcile-body-zero"
          className="mb-3 italic text-slate-400"
        >
          {RECONCILE_BODY_ZERO}
        </p>
      ) : (
        <p className="mb-3">
          {RECONCILE_BODY_PREFIX}
          <strong
            data-testid="governance-tile-reconcile-tasks-total"
            className="text-slate-100"
          >
            {tasksTotal === null ? PENDING : tasksTotal}
          </strong>
          {RECONCILE_BODY_SUFFIX}
        </p>
      )}
      <ul className="space-y-1.5">
        {RECONCILE_ITEMS.map((item) => {
          const resolved = workingSummary ? item.extract(workingSummary) : null;
          const pending = resolved === null;
          const strongText = pending
            ? PENDING
            : `${resolved.numerator} of ${resolved.denominator}`;
          const testId = pending
            ? `governance-tile-reconcile-item-${item.key}-pending`
            : `governance-tile-reconcile-item-${item.key}`;
          return (
            <li
              key={item.key}
              data-testid={testId}
              className="flex gap-2"
            >
              <span className="text-cyan-400 flex-shrink-0" aria-hidden="true">·</span>
              <span>
                <strong className="text-slate-100">{strongText}</strong> {item.rest}
              </span>
            </li>
          );
        })}
      </ul>
      {loading ? (
        <p
          data-testid="governance-tile-reconcile-loading"
          className="mt-2 text-[10px] italic text-slate-500"
        >
          Live counters loading&hellip;
        </p>
      ) : null}
      {error ? (
        <p
          data-testid="governance-tile-reconcile-error"
          className="mt-2 text-[10px] text-rose-400"
        >
          Live counter fetch failed. Values show pending until the endpoint recovers.
        </p>
      ) : null}
    </div>
  );
}
